import api from "../api/client";
import { fetchPhotoDataUrl } from "../components/AuthImage/AuthImage";

/**
 * The card photo window is 17.5mm x 22mm. Cropping straight to that ratio
 * avoids cropping twice (once to 35:45, then again by object-fit inside the
 * window), which would throw away more of the image than necessary.
 */
export const PHOTO_RATIO = 17.5 / 22;

const MAX_BYTES = 8 * 1024 * 1024;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];

const UNSPLASH_ACCESS_KEY = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;
const UNSPLASH_RANDOM_API = "https://api.unsplash.com/photos/random";

/**
 * Centre-crops a loaded image to the passport aspect ratio without
 * distorting it: the shorter axis fills the frame and the overflow on the
 * longer axis is trimmed equally from both sides (a "cover" fit), then
 * re-encodes at a fixed pixel size so every photo — uploaded or fetched —
 * lands on the card at the same dimensions.
 */
function drawCoverToDataUrl(img, targetHeight) {
  const targetWidth = Math.round(targetHeight * PHOTO_RATIO);
  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, targetWidth, targetHeight);

  const scale = Math.max(targetWidth / img.width, targetHeight / img.height);
  const drawW = img.width * scale;
  const drawH = img.height * scale;
  ctx.drawImage(
    img,
    (targetWidth - drawW) / 2,
    (targetHeight - drawH) / 2,
    drawW,
    drawH
  );

  return canvas.toDataURL("image/jpeg", 0.92);
}

/** Reads an uploaded photo and centre-crops it to the passport aspect ratio. */
export function cropToPassport(file, targetHeight = 900) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("No file selected."));
    if (!ACCEPTED.includes(file.type)) {
      return reject(new Error("Please upload a JPG, PNG or WEBP image."));
    }
    if (file.size > MAX_BYTES) {
      return reject(new Error("Image is larger than 8 MB. Please pick a smaller file."));
    }

    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        resolve(drawCoverToDataUrl(img, targetHeight));
      } catch (err) {
        reject(new Error("That file could not be processed."));
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file could not be read as an image."));
    };

    img.src = url;
  });
}

/**
 * Pulls a random headshot-style photo matching the requested gender.
 *
 * This searches Unsplash for portraits explicitly tagged/described as
 * Indian, rather than drawing from an unfiltered global pool: no free,
 * keyless face API can filter by ethnicity (randomuser.me's own nat=IN, for
 * example, only changes the generated name — comparing nat=IN and nat=US
 * side by side returns photos from the exact same ~100-face pool), so a
 * search-based real photo library is the closest achievable match to "looks
 * Indian" without standing up a paid service. Requires a free Unsplash
 * Access Key — see .env.example.
 */
/**
 * Draws a portrait from the shared pool - photos other users uploaded and
 * left public. Resolves to null when the pool is empty or unreachable, so the
 * caller falls through to Unsplash rather than failing.
 *
 * Only public rows are eligible; that filter is applied server-side (see
 * routes/photos.js) and re-checked when the bytes are fetched, so a private
 * photo can never surface here.
 */
export function fetchPoolPhoto() {
  return api
    .get("/api/photos/random")
    .then((res) =>
      res?.photo?.id ? fetchPhotoDataUrl(`/api/photos/${res.photo.id}/raw`) : null
    )
    .catch(() => null);
}

export function fetchRandomPhoto(gender, targetHeight = 900) {
  if (!UNSPLASH_ACCESS_KEY) {
    return Promise.reject(
      new Error(
        "Random photo needs a free Unsplash API key. Copy .env.example to .env, add your key to REACT_APP_UNSPLASH_ACCESS_KEY, then restart the dev server."
      )
    );
  }

  const resolvedGender =
    gender === "male" || gender === "female"
      ? gender
      : Math.random() < 0.5
      ? "male"
      : "female";

  const query =
    resolvedGender === "male"
      ? "indian man portrait face"
      : "indian woman portrait face";

  const params = new URLSearchParams({
    query,
    orientation: "portrait",
    content_filter: "high",
  });

  return fetch(`${UNSPLASH_RANDOM_API}?${params}`, {
    headers: { Authorization: `Client-ID ${UNSPLASH_ACCESS_KEY}` },
  })
    .then((res) => {
      if (res.status === 401 || res.status === 403) {
        throw new Error("Unsplash rejected the API key — check REACT_APP_UNSPLASH_ACCESS_KEY in .env.");
      }
      if (res.status === 429) {
        throw new Error("Unsplash's free rate limit (50/hour) was hit. Try again shortly.");
      }
      if (!res.ok) throw new Error("Could not reach the photo service.");
      return res.json();
    })
    .then((photo) => {
      const url = photo?.urls?.regular;
      if (!url) throw new Error("No photo was returned.");

      // Required by Unsplash's API guidelines whenever a photo is used
      // (not just displayed in search results); fire-and-forget.
      if (photo?.links?.download_location) {
        fetch(photo.links.download_location, {
          headers: { Authorization: `Client-ID ${UNSPLASH_ACCESS_KEY}` },
        }).catch(() => {});
      }

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            resolve(drawCoverToDataUrl(img, targetHeight));
          } catch (err) {
            reject(new Error("Could not process the fetched photo."));
          }
        };
        img.onerror = () => reject(new Error("Could not load the fetched photo."));
        img.src = url;
      });
    });
}
